import React, { useEffect, useRef, useState, useCallback } from 'react';
import editorBridge from './editorBridge';
import {
  createFabricCanvas,
  loadDesignPayload,
  fitCanvasToContainer,
  exportCanvasPng,
  openNewCanvas,
  prepareImageDataForSave,
} from './canvasEditor';
import {
  DEFAULT_CANVAS_WIDTH,
  DEFAULT_CANVAS_HEIGHT,
  buildAppLayout,
  getDesignPayload,
} from './layoutBuilder';
import uploadApi from '../../api/uploadApi';
import { EditorProvider } from './editor/EditorContext';
import EditorShell from './editor/EditorShell';
import { useFabricSelection } from './editor/useFabricSelection';
import { deleteObjectWithLinks } from './editor/fabricHelpers';

function TemplateEditorInner({
  template,
  onTemplateChange,
  canvasRef,
  fabricRef,
  viewportRef,
  originalRef,
}) {
  const [activeTool, setActiveTool] = useState('basics');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [ready, setReady] = useState(false);
  const [selection, setSelection] = useState(null);
  const [layersOpen, setLayersOpen] = useState(false);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [stickerCategories, setStickerCategories] = useState([]);
  const [layerTick, setLayerTick] = useState(0);
  const refreshLayers = useCallback(() => setLayerTick((t) => t + 1), []);

  const applyPayloadToParent = useCallback((payload) => {
    if (onTemplateChange) onTemplateChange((prev) => ({ ...prev, ...payload }));
  }, [onTemplateChange]);

  const handleSave = useCallback(async ({ cb, thumbnailOnly } = {}) => {
    const fc = fabricRef.current;
    const original = originalRef.current;
    if (!fc || !original?.width) {
      if (cb) cb();
      return;
    }

    try {
      fc.setZoom(1);
      fc.setWidth(original.width);
      fc.setHeight(original.height);
      fc.renderAll();

      let png;
      try {
        png = exportCanvasPng(fc, 1);
      } catch {
        png = template?.previewurl || '';
      }
      const path = await uploadApi.upload(png);

      if (thumbnailOnly) {
        editorBridge.templateSaved(path);
        if (cb) cb(null, { previewurl: path });
        return;
      }

      const design = getDesignPayload(fc, original, {});
      const layoutObj = JSON.parse(design.layout);
      const imageData = await prepareImageDataForSave(fc, uploadApi.upload);
      const applayout = buildAppLayout(
        fc,
        original,
        layoutObj.background || bgColor,
        path,
        imageData
      );

      const payload = {
        ...design,
        previewurl: path,
        imageurl: path,
        applayout: {
          ...applayout,
          previewImage: path,
        },
      };

      applyPayloadToParent(payload);
      editorBridge.templateSaved(path);
      if (cb) cb(null, payload);
    } catch (err) {
      console.error(err);
      if (cb) cb(err);
    } finally {
      fitCanvasToContainer(fabricRef.current, viewportRef.current, originalRef.current);
    }
  }, [applyPayloadToParent, bgColor, template?.previewurl, fabricRef, originalRef, viewportRef]);

  useEffect(() => {
    if (!canvasRef.current) return undefined;

    const fc = createFabricCanvas(canvasRef.current);
    fabricRef.current = fc;

    const onResize = () => {
      fitCanvasToContainer(fc, viewportRef.current, originalRef.current);
    };
    window.addEventListener('resize', onResize);

    const onKey = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      const active = fc.getActiveObject();
      if (active) {
        e.preventDefault();
        deleteObjectWithLinks(fc, active);
        refreshLayers();
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKey);
      fc.dispose();
      fabricRef.current = null;
    };
  }, [canvasRef, fabricRef, viewportRef, originalRef, refreshLayers]);

  useEffect(() => {
    const fc = fabricRef.current;
    if (!fc || !template) return;

    const load = async () => {
      setReady(false);
      try {
        if (template.layout && template.layout !== '{}') {
          const original = await loadDesignPayload(fc, template);
          originalRef.current = original;
          const parsed = JSON.parse(
            typeof template.layout === 'string' ? template.layout : JSON.stringify(template.layout)
          );
          if (parsed.background) setBgColor(parsed.background);
        } else {
          const original = openNewCanvas(fc, DEFAULT_CANVAS_WIDTH, DEFAULT_CANVAS_HEIGHT);
          originalRef.current = original;
        }
        fitCanvasToContainer(fc, viewportRef.current, originalRef.current);
        setReady(true);
        editorBridge.editorLoaded();
      } catch (err) {
        console.error('Canvas load failed', err);
        const original = openNewCanvas(fc, DEFAULT_CANVAS_WIDTH, DEFAULT_CANVAS_HEIGHT);
        originalRef.current = original;
        fitCanvasToContainer(fc, viewportRef.current, originalRef.current);
        setReady(true);
      }
    };
    load();
  }, [template?.id, fabricRef, originalRef, viewportRef]);

  useEffect(() => {
    const unsubSave = editorBridge.on('editor.save', handleSave);
    const unsubUpdate = editorBridge.on('editor.update', () => handleSave({ thumbnailOnly: true }));
    return () => {
      unsubSave();
      unsubUpdate();
    };
  }, [handleSave]);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ''}/pixie/config.json`)
      .then((r) => r.json())
      .then((cfg) => setStickerCategories(cfg.stickers || []))
      .catch(() => setStickerCategories([]));
  }, []);

  useFabricSelection(fabricRef, { setSelection, setActiveTool, refreshLayers });

  return (
    <EditorProvider
      fabricRef={fabricRef}
      originalRef={originalRef}
      viewportRef={viewportRef}
      ready={ready}
      bgColor={bgColor}
      setBgColor={setBgColor}
      activeTool={activeTool}
      setActiveTool={setActiveTool}
      selection={selection}
      setSelection={setSelection}
      layersOpen={layersOpen}
      setLayersOpen={setLayersOpen}
      zoomPercent={zoomPercent}
      setZoomPercent={setZoomPercent}
      stickerCategories={stickerCategories}
      refreshLayers={refreshLayers}
      currentTemplateId={template?.id}
    >
      <div className="at-editor-canvas-wrap" ref={viewportRef}>
        <EditorShell canvasRef={canvasRef} ready={ready} layerTick={layerTick} />
      </div>
    </EditorProvider>
  );
}

export default function TemplateEditorCanvas(props) {
  const canvasRef = useRef(null);
  const viewportRef = useRef(null);
  const fabricRef = useRef(null);
  const originalRef = useRef({ width: DEFAULT_CANVAS_WIDTH, height: DEFAULT_CANVAS_HEIGHT });

  return (
    <TemplateEditorInner
      {...props}
      canvasRef={canvasRef}
      fabricRef={fabricRef}
      viewportRef={viewportRef}
      originalRef={originalRef}
    />
  );
}
